import {FocusTrap, Textarea} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useState, useCallback} from 'react';

import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {getUserMessage} from 'util/Net';

import {FeedbackContextItem} from './LLMFeedbackConstants';
import {submitLLMFeedback} from './LLMFeedbackDAO';

interface FeedbackFormConfig {
  title: string;
  requiredContextItems: Array<FeedbackContextItem>;
  optionalContextItems: Array<FeedbackContextItem>;
  score?: number | null;
}

interface FeedbackFormState {
  disabled: boolean;
  submitForm: () => Promise<void>;
  formProps: Omit<LLMFeedbackFormContentsProps, 'isPositiveFeedback'>;
  clearForm: () => void;
}

export function useFeedbackForm({
  title,
  requiredContextItems,
  optionalContextItems,
  score,
}: FeedbackFormConfig): FeedbackFormState {
  const [llmFeedback, setLLMFeedback] = useState<string>('');
  const [selectedOptionalItemNames, setSelectedOptionalItemNames] = useState<Array<string>>(
    optionalContextItems.map((item) => item.name)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const submitForm = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await submitLLMFeedback(
        title,
        llmFeedback,
        [
          ...requiredContextItems,
          ...optionalContextItems.filter((item) => selectedOptionalItemNames.includes(item.name)),
        ],
        score
      ).then(() => {
        setErrorMessage(null);
      });
    } catch (e) {
      setErrorMessage(getUserMessage(e) ?? 'An unexpected error occurred. Please try again.');
      e.__suppress_notification = true; // no toast.
      throw e;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    llmFeedback,
    selectedOptionalItemNames,
    requiredContextItems,
    optionalContextItems,
    title,
    score,
    setErrorMessage,
  ]);
  const disabled = llmFeedback.length === 0 || isSubmitting;

  const clearForm = useCallback(() => {
    setLLMFeedback('');
    setSelectedOptionalItemNames([]);
    setErrorMessage(null);
  }, [setLLMFeedback, setSelectedOptionalItemNames, setErrorMessage]);

  const formProps = {
    llmFeedback,
    onChange: setLLMFeedback,
    selectedOptionalItemNames,
    setSelectedOptionalItemNames,
    disabled,
    submitForm,
    errorMessage,
    optionalContextItems,
  };

  return {
    formProps,
    clearForm,
    disabled,
    submitForm,
  };
}

interface LLMFeedbackFormContentsProps {
  llmFeedback: string;
  onChange: (llmFeedback: string) => void;
  isPositiveFeedback: boolean;
  errorMessage: string | null;
  textAreaRef?: React.RefObject<HTMLTextAreaElement>;
  optionalContextItems: Array<FeedbackContextItem>;
  selectedOptionalItemNames: Array<string>;
  setSelectedOptionalItemNames: React.Dispatch<React.SetStateAction<Array<string>>>;
}

export function LLMFeedbackFormContents({
  llmFeedback,
  onChange,
  isPositiveFeedback,
  errorMessage,
  textAreaRef,
  optionalContextItems,
  selectedOptionalItemNames,
  setSelectedOptionalItemNames,
}: LLMFeedbackFormContentsProps): JSX.Element {
  return (
    <FocusTrap active={true}>
      <div className={css(styles.paddedGrid)}>
        <div>
          <label htmlFor="feedback">
            {isPositiveFeedback
              ? 'Help us improve by sharing more about your experience.'
              : 'Let us know what went wrong or how we can improve.'}
          </label>
          <Textarea
            size="md"
            ref={textAreaRef}
            autosize={true}
            minRows={6}
            value={llmFeedback}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            name="feedback"
          />
        </div>
        {optionalContextItems.length > 0 && (
          <div>
            <h4>Optional context</h4>
            {optionalContextItems.map((context) => (
              <div key={context.name} className={css(styles.optionalItem)}>
                <CheckboxInput
                  checked={selectedOptionalItemNames.includes(context.name)}
                  id={`select-${context.name}`}
                  label={context.name}
                  onChange={() => {
                    setSelectedOptionalItemNames((prev) => {
                      if (prev.includes(context.name)) {
                        return prev.filter((item) => item !== context.name);
                      }
                      return [...prev, context.name];
                    });
                  }}
                />
                {selectedOptionalItemNames.includes(context.name) && (
                  <pre className={css(styles.limitedHeight)}>{context.value}</pre>
                )}
              </div>
            ))}
          </div>
        )}
        {errorMessage && <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />}
      </div>
    </FocusTrap>
  );
}

const styles = StyleSheet.create({
  optionalItem: {
    display: 'grid',
    gap: 8,
  },
  paddedGrid: {
    display: 'grid',
    gap: 12,
    fontSize: 14,
  },
  limitedHeight: {
    maxHeight: 240,
    overflowY: 'auto',
  },
});
